import "./StockModal.scss";
import { useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { collection, doc, getDocs, query, runTransaction, where } from "firebase/firestore";
import StockModal from "./StockModal";
import { db } from "../../../../firebase";
import { normalizeStockLinks } from "../../../../utility/stockManagement";

const FIRESTORE_MAX_TRANSACTION_OPERATIONS = 500;
const FIRESTORE_TRANSACTION_SAFE_OPERATIONS = FIRESTORE_MAX_TRANSACTION_OPERATIONS - 50;

export default function DeleteStockItemsModal({
  open,
  onClose,
  onDeleted,
  stockItemsIds = [],
  venueId,
}) {
  const { t } = useTranslation("stockManagement");
  const alert = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    if (isSubmitting) return;
    onClose();
  };

  const submitHandler = async () => {
    try {
      setIsSubmitting(true);

      const stockItemIdsSet = new Set(stockItemsIds);
      const linkedDocs = (
        await Promise.all([
          getDocs(query(collection(db, "products"), where("uid", "==", venueId))),
          getDocs(query(collection(db, "OptionProducts"), where("uid", "==", venueId))),
        ])
      )
        .flatMap((linkedDoc) => linkedDoc.docs)
        .filter((document) =>
          normalizeStockLinks(document.data()).some((link) => stockItemIdsSet.has(link.stockItemId)),
        );

      if (linkedDocs.length + stockItemsIds.length > FIRESTORE_TRANSACTION_SAFE_OPERATIONS) {
        alert.show(t("delete_stock_modal_too_many_items_error"), { type: "error" });
        return;
      }

      await runTransaction(db, async (transaction) => {
        linkedDocs.forEach((document) => {
          const nextStockLinks = normalizeStockLinks(document.data()).filter(
            (link) => !stockItemIdsSet.has(link.stockItemId),
          );
          const primaryStockLink = nextStockLinks[0];
          transaction.update(document.ref, {
            stockLinks: nextStockLinks,
            stockItemId: primaryStockLink?.stockItemId || null,
            quantityPerSale: primaryStockLink?.quantityPerSale || null,
            outOfStockBehavior: nextStockLinks.length > 0 ? document.data().outOfStockBehavior || "alert" : null,
          });
        });

        stockItemsIds.forEach((stockItemId) => {
          transaction.delete(doc(db, "StockItems", stockItemId));
        });
      });

      await onDeleted();
    } catch (error) {
      alert.show(t("delete_stock_modal_submit_error"), { type: "error" });
      console.error("Error deleting stock item(s):", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StockModal
      open={open}
      onClose={closeModal}
      onConfirm={submitHandler}
      isLoading={isSubmitting}
      title={t("delete_stock_modal_title", { count: stockItemsIds.length })}
    >
      <div className="stock-action-modal-products">
        <div className="stock-delete-warning-card">
          <div className="stock-delete-warning-card-content">
            <p className="stock-action-modal-warning-title">
              {t("stock_delete_modal_warning_title")}
            </p>
            <p className="stock-action-modal-products-subtitle">
              {t("delete_stock_modal_subtitle", { count: stockItemsIds.length })}
            </p>
            <p className="stock-action-modal-products-subtitle">
              {t("delete_stock_modal_warning")}
            </p>
          </div>
        </div>
      </div>
    </StockModal>
  );
}
